import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool
} from 'amazon-cognito-identity-js';

const AuthContext = createContext();

const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID || '';
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID || '';

const GROUP_ROLE_MAP = {
  admin: 'ops_admin',
  vp: 'ops_vp',
  sales_vp: 'ops_sales_vp',
  sales_agent: 'sales_agent',
  sales: 'sales_agent',
  field: 'ops_field'
};

const GROUP_ROLE_PRIORITY = ['admin', 'vp', 'sales_vp', 'sales_agent', 'sales', 'field'];

function normalizeGroup(value) {
  return String(value || '').trim().toLowerCase();
}

function decodeJwt(token) {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = payload.length % 4;
    if (padding) {
      payload += '='.repeat(4 - padding);
    }
    const json = atob(payload);
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
}

function getRoleFromGroups(groups) {
  if (!groups || !Array.isArray(groups)) return 'ops_field';
  const normalized = groups.map(normalizeGroup);
  for (const group of GROUP_ROLE_PRIORITY) {
    if (!normalized.includes(group)) continue;
    if (GROUP_ROLE_MAP[group]) return GROUP_ROLE_MAP[group];
  }
  return 'ops_field';
}

function buildUser(session) {
  if (!session) return null;
  const idToken = session.getIdToken?.().getJwtToken?.();
  const accessToken = session.getAccessToken?.().getJwtToken?.();
  const payload = decodeJwt(idToken) || {};
  const email = payload.email || payload['cognito:username'] || '';
  const groups = payload['cognito:groups'] || [];
  const role = getRoleFromGroups(groups);
  return { email, role, groups, idToken, accessToken };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingAttributes, setPendingAttributes] = useState(null);

  const userPool = useMemo(() => {
    if (!USER_POOL_ID || !USER_POOL_CLIENT_ID) return null;
    return new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: USER_POOL_CLIENT_ID
    });
  }, []);

  useEffect(() => {
    if (!userPool) {
      setLoading(false);
      return;
    }
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      setUser(null);
      setLoading(false);
      return;
    }
    currentUser.getSession((err, session) => {
      if (err || !session?.isValid?.()) {
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(buildUser(session));
      setLoading(false);
    });
  }, [userPool]);

  const login = async (email, password) => {
    if (!userPool) {
      throw new Error('Auth not configured');
    }
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const nextUser = buildUser(session);
          setUser(nextUser);
          setPendingUser(null);
          setPendingAttributes(null);
          resolve(nextUser);
        },
        newPasswordRequired: (userAttributes = {}) => {
          const cleaned = { ...userAttributes };
          delete cleaned.email;
          delete cleaned.email_verified;
          delete cleaned.phone_number;
          delete cleaned.phone_number_verified;
          delete cleaned.preferred_username;
          delete cleaned.sub;
          setPendingUser(cognitoUser);
          setPendingAttributes(cleaned);
          resolve({ challenge: 'NEW_PASSWORD_REQUIRED' });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const completeNewPassword = async (newPassword) => {
    if (!pendingUser) {
      throw new Error('No pending password reset');
    }
    return new Promise((resolve, reject) => {
      pendingUser.completeNewPasswordChallenge(
        newPassword,
        pendingAttributes && Object.keys(pendingAttributes).length > 0 ? pendingAttributes : {},
        {
          onSuccess: (session) => {
            const nextUser = buildUser(session);
            setUser(nextUser);
            setPendingUser(null);
            setPendingAttributes(null);
            resolve(nextUser);
          },
          onFailure: (err) => {
            reject(err);
          }
        }
      );
    });
  };

  const logout = () => {
    if (!userPool) {
      setUser(null);
      return;
    }
    const currentUser = userPool.getCurrentUser();
    if (currentUser) currentUser.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, completeNewPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
